import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { push } from 'connected-react-router';

import { openModalAction } from '../../../actions/modal';
import { PageHeader } from '../../../components/PageHeader';
import { Button } from '../../../components/PageHeader/Button';
import { TextInput } from '../../../components/PageHeader/TextInput';
import { HEADER_BUTTON_BLUE, TIME_FORMAT_12_UPPERCASE } from '../../../constants';
import Table3, { columnHelper } from '../../../containers/Table/Table3';
import Strings from '../../../Strings';
import { toolTipWithValue } from '../../../utils';
import { actions } from '../../Organizations/redux/actions';
import { ORGANIZATION_DEVICES } from '../../Organizations/redux/constants';
import { BatteryWithChargingStatus } from '../../../components/ChargingStatus/ChargingStatus';

function HubManage(props) {
  const globalPageSize = useGlobalPageSize();
  const pageRequest = React.useRef({
    offset: 0,
    limit: globalPageSize,
    search: '',
    filterBy: 'hub',
  });
  React.useEffect(() => {
    if (props.history?.location?.state?.params?.offset) {
      pageRequest.current.offset = props.history?.location?.state?.params?.offset;
    }
    props.loadCaps(pageRequest.current);

    return () => props.clearData();
  }, []);

  const onSearchQueryChange = (query) => {
    pageRequest.current.offset = 0;
    pageRequest.current.search = query;
    props.loadCaps(pageRequest.current);
  };

  const onTextInputChange = (e) => {
    onSearchQueryChange(e.target.value);
  };

  const onRowSelected = (device) => {
    props.onNavigate(`/org-devices/hub/${device.cap_id}/devices`, { offset: pageRequest.current.offset });
  };

  const onOffsetChange = React.useCallback((offset) => {
    pageRequest.current.offset = offset;
    props.loadCaps(pageRequest.current);
  }, []);

  const onPageSizeChange = React.useCallback((newSize) => {
    pageRequest.current.offset = 0;
    pageRequest.current.limit = newSize;
    props.loadCaps(pageRequest.current);
  }, []);

  const getHeaderComponents = () => {
    const { onAttachCapToAdmin } = props;
    return (
      <React.Fragment>
        <TextInput class="search" placeholder={Strings.search} onChange={onTextInputChange} />
        <Button
          class={HEADER_BUTTON_BLUE}
          onClick={() => onAttachCapToAdmin(pageRequest.current)}
          title={Strings.attachCap}
        />
      </React.Fragment>
    );
  };

  const columns = React.useMemo(() => [
    columnHelper.accessor('cap_id', {
      header: Strings.hubId,
      size: 150,
    }),
    columnHelper.accessor('imei', {
      header: Strings.imei,
      size: 150,
    }),
    columnHelper.accessor('timestamp', {
      header: Strings.lastActivity,
      cell: info =>
        moment(moment.utc(moment.unix(info.getValue())))
          .local()
          .format(TIME_FORMAT_12_UPPERCASE),
      size: 180,
    }),
    columnHelper.accessor('battery', {
      header: Strings.battery.battery,
      cell: info => (
        <BatteryWithChargingStatus
          battery={info.getValue()}
          batteryMeasuredAt={info.row.original.battery_measured_at}
          chargingStatus={info.row.original.charging_status}
        />
      ),
      size: 100,
    }),
    columnHelper.accessor('signal_strength', {
      header: Strings.signalStrength,
      size: 120,
    }),
    columnHelper.accessor('location', {
      header: Strings.location,
      cell: info => toolTipWithValue(info.getValue() || '-'),
      size: 150,
    }),
    columnHelper.accessor('devices', {
      header: Strings.devices,
      size: 100,
    }),
    columnHelper.accessor('is_sample', {
      header: Strings.deviceInvoices.sample,
      cell: info => (info.getValue() ? Strings.yes : Strings.no),
      size: 100,
    }),
    columnHelper.accessor('status', {
      header: Strings.status,
      size: 100,
    }),
  ], []);

  const { devices, isLoading, pagination } = props;

  return (
    <div className="device-tab-content">
      <PageHeader
        title={Strings.devicesTab}
        getHeaderComponents={() => getHeaderComponents()}
      />
      {devices && (
        <Table3
          name="cap-list"
          data={devices}
          columns={columns}
          onRowClick={onRowSelected}
          onOffsetChange={onOffsetChange}
          onPageSizeChange={onPageSizeChange}
          pagination={pagination}
          isLoading={isLoading}
        />
      )}
    </div>
  );
}

HubManage.propTypes = {
  clearData: PropTypes.func,
  devices: PropTypes.any,
  history: PropTypes.shape({
    location: PropTypes.shape({ state: PropTypes.shape({ params: PropTypes.shape({ offset: PropTypes.any }) }) }),
  }),
  isLoading: PropTypes.any,
  loadCaps: PropTypes.func,
  onAttachCapToAdmin: PropTypes.func,
  onNavigate: PropTypes.func,
  pagination: PropTypes.shape({
    offset: PropTypes.number,
    total: PropTypes.number,
  }),
};

const mapStateToProps = state => {
  const { organizations } = state.superUser;
  return {
    role: state.auth.role,
    devices: organizations && organizations?.devices,
    isLoading: organizations && organizations?.isLoading,
    pagination: organizations && organizations?.pagination,
  };
};

const mapDispatchToProps = dispatch => ({
  onNavigate: (path, params) =>
    dispatch(
      push({
        pathname: path,
        state: { params },
      }),
    ),
  loadCaps: pageRequest => dispatch(actions.getDevices(pageRequest)),
  clearData: () => dispatch(actions.clearData()),
  onAttachCapToAdmin: req =>
    dispatch(
      openModalAction('attach-cap-to-org', {
        action: actions.getDevices,
        actionType: ORGANIZATION_DEVICES,
        type: 'hub',
        request: req,
      }),
    ),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(HubManage));
